import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Form, Button, Table, Card, Spinner, Badge } from "react-bootstrap";
import Layout from "views/universal/Layout/Layout";
import { barometreOperations, barometreSelector } from "state/ducks/store/barometre";
import VagueReview from "./VagueReview";
import Employes from "./Employes";
import Help from "./Help";

const STATUT_VARIANT = {
  reception: "secondary",
  classe: "info",
  valide: "primary",
  exporte: "success",
};

const Barometre = (props) => {
  const dispatch = useDispatch();
  const vagues = useSelector(barometreSelector.getVaguesSelector);

  const [selectedId, setSelectedId] = useState(null);
  const [showEmployes, setShowEmployes] = useState(false);
  const [file, setFile] = useState(null);
  const [libelle, setLibelle] = useState("");
  const [date, setDate] = useState("");
  const [template, setTemplate] = useState("auto");
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState(null);

  useEffect(() => {
    dispatch(barometreOperations.getVagues());
  }, [dispatch]);

  const handleImport = () => {
    if (!file || !libelle.trim()) {
      setUploadMsg({ ok: false, text: "Fichier et libellé requis." });
      return;
    }
    setUploading(true);
    setUploadMsg(null);
    barometreOperations
      .uploadBarometre({ file, libelle: libelle.trim(), date, template })
      .then((result) => {
        setUploading(false);
        setUploadMsg({ ok: true, text: result.message });
        setFile(null);
        setLibelle("");
        dispatch(barometreOperations.getVagues());
      })
      .catch((err) => {
        setUploading(false);
        const msg = err?.response?.data?.result?.message || err?.message || "Échec de l'import.";
        setUploadMsg({ ok: false, text: msg });
      });
  };

  const handleDelete = (v) => {
    const ok = window.confirm(
      `Supprimer définitivement la vague « ${v.libelle} » (${v.nbReponses} réponses) ?\n\n` +
        "Toutes ses réponses, verbatims et thèmes seront effacés. Cette action est irréversible."
    );
    if (!ok) return;
    dispatch(barometreOperations.deleteVague(v.id));
  };

  if (showEmployes) {
    return (
      <Layout location={props.location?.search}>
        <Employes onBack={() => setShowEmployes(false)} />
      </Layout>
    );
  }

  if (selectedId) {
    return (
      <Layout location={props.location?.search}>
        <Button variant="link" onClick={() => setSelectedId(null)} style={{ paddingLeft: 0 }}>
          ← Retour à la liste des vagues
        </Button>
        <VagueReview id={selectedId} />
      </Layout>
    );
  }

  return (
    <Layout location={props.location?.search}>
      <h3 style={{ color: "#1d1d1b" }}>
        Baromètre — dépouillement
        <Help title="Comment ça marche ?" placement="bottom">
          Le dépouillement se fait en 4 temps :
          <br />
          <b>1. Importer</b> le fichier Excel des réponses.
          <br />
          <b>2. Classer avec l'IA</b> : elle dégage les thèmes de la vague et range chaque réponse
          dans un seul thème (son sujet principal), sans réécrire les verbatims.
          <br />
          <b>3. Relire & corriger</b> : tout reste modifiable, tu valides chaque réponse.
          <br />
          <b>4. Synthèse & export Excel.</b>
          <br />
          Le ressenti « je me sens » est gardé d'une vague à l'autre pour suivre son évolution.
        </Help>
      </h3>

      <Card className="mb-4" style={{ marginTop: 16 }}>
        <Card.Body>
          <Card.Title>
            Importer une vague
            <Help title="Importer une vague">
              Une « vague » = un baromètre à une date donnée (ex. Juillet 2025). Le{" "}
              <b>template</b> indique la structure de colonnes du fichier (BtoC+BtoB ou BtoB rush) —
              choisis celui qui correspond à l'onglet de ton Excel. Le statut RH (Employé / CEA /
              Cadre) n'est pas deviné : tu le renseignes ensuite.
            </Help>
          </Card.Title>
          <Form>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
              <Form.Group style={{ minWidth: 220 }}>
                <Form.Label>Fichier xlsx</Form.Label>
                <Form.Control
                  type="file"
                  accept=".xlsx"
                  onChange={(e) => setFile(e.target.files[0] || null)}
                />
              </Form.Group>
              <Form.Group style={{ minWidth: 180 }}>
                <Form.Label>Libellé de la vague</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Juillet 2025"
                  value={libelle}
                  onChange={(e) => setLibelle(e.target.value)}
                />
              </Form.Group>
              <Form.Group style={{ minWidth: 160 }}>
                <Form.Label>Date de la vague</Form.Label>
                <Form.Control type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              </Form.Group>
              <Form.Group style={{ minWidth: 200 }}>
                <Form.Label>Template</Form.Label>
                <Form.Control as="select" value={template} onChange={(e) => setTemplate(e.target.value)}>
                  <option value="auto">Automatique (détecter)</option>
                  {(vagues.templates || []).map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.label}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
              <Button onClick={handleImport} disabled={uploading} style={{ height: 38 }}>
                {uploading ? <Spinner animation="border" size="sm" /> : "Importer"}
              </Button>
            </div>
          </Form>
          {uploadMsg && (
            <div style={{ marginTop: 10, color: uploadMsg.ok ? "#429853" : "#c0392b" }}>
              {uploadMsg.text}
            </div>
          )}
        </Card.Body>
      </Card>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h5 style={{ margin: 0 }}>Vagues</h5>
        <Button variant="outline-secondary" size="sm" onClick={() => setShowEmployes(true)}>
          Employés &amp; statuts
        </Button>
      </div>
      {vagues.isLoading ? (
        <Spinner animation="border" />
      ) : (
        <Table hover responsive>
          <thead>
            <tr>
              <th>Vague</th>
              <th>Date</th>
              <th>Type</th>
              <th>Réponses</th>
              <th>
                Statut
                <Help title="Statut de la vague" placement="left">
                  Avancement du dépouillement : <b>Réception</b> (importé) →{" "}
                  <b>Classé</b> (IA passée) → <b>Validé</b> (relu par toi) → <b>Exporté</b>.
                </Help>
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(vagues.data || []).map((v) => (
              <tr key={v.id}>
                <td>{v.libelle}</td>
                <td>{v.date}</td>
                <td>{v.typeFr}</td>
                <td>{v.nbReponses}</td>
                <td>
                  <Badge variant={STATUT_VARIANT[v.statut] || "secondary"}>{v.statutFr}</Badge>
                </td>
                <td>
                  <Button size="sm" onClick={() => setSelectedId(v.id)}>
                    Ouvrir
                  </Button>{" "}
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => handleDelete(v)}
                    title="Supprimer la vague"
                  >
                    Supprimer
                  </Button>
                </td>
              </tr>
            ))}
            {(!vagues.data || vagues.data.length === 0) && (
              <tr>
                <td colSpan={6} style={{ textAlign: "center", color: "#888" }}>
                  Aucune vague importée pour le moment.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}
    </Layout>
  );
};

export default Barometre;
